import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import paymentService from '../../../services/payment/payment.service';
import { PaymentRecord } from '../../../types/payment.types';
import Datatable from '../../shared/dataTable';
// Toast notifications will be handled by the error boundary or custom notification system
import moment from 'moment';
import dayjs from 'dayjs';
import PaymentFilters from './components/PaymentFilters';

// CSS-in-JS styles for animations and modern cards
const modalStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideInDown {
    from {
      opacity: 0;
      transform: translateY(-50px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes statsCardHover {
    from { transform: translateY(0px); }
    to { transform: translateY(-4px); }
  }
  
  .invoice-modal-backdrop {
    z-index: 1055;
  }
  
  .bg-success-light {
    background-color: rgba(25, 135, 84, 0.1) !important;
  }
  
  .bg-warning-light {
    background-color: rgba(255, 193, 7, 0.1) !important;
  }
  
  .bg-danger-light {
    background-color: rgba(220, 53, 69, 0.1) !important;
  }
  
  .bg-info-light {
    background-color: rgba(13, 202, 240, 0.1) !important;
  }
  
  .bg-secondary-light {
    background-color: rgba(108, 117, 125, 0.1) !important;
  }
  
  .payment-amount:hover {
    transform: scale(1.02);
    transition: transform 0.2s ease;
  }
  
  .user-limit-display:hover {
    transform: translateX(2px);
    transition: transform 0.2s ease;
  }

  /* Ultra Modern Stats Cards */
  .stats-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 20px;
    box-shadow: 
      0 10px 25px -5px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.8);
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    position: relative;
    height: 160px;
    backdrop-filter: blur(10px);
  }
  
  .stats-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: var(--card-gradient);
    border-radius: 20px 20px 0 0;
  }
  
  .stats-card::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, var(--card-color) 0%, transparent 70%);
    opacity: 0.03;
    pointer-events: none;
  }
  
  .stats-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 25px 50px -12px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  .stats-card-primary {
    --card-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    --card-color: #667eea;
  }
  
  .stats-card-success {
    --card-gradient: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    --card-color: #11998e;
  }
  
  .stats-card-warning {
    --card-gradient: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
    --card-color: #ff6b6b;
  }
  
  .stats-card-info {
    --card-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    --card-color: #4facfe;
  }
  
  .stats-info {
    display: flex;
    align-items: center;
    padding: 32px 28px;
    height: 100%;
    z-index: 1;
    position: relative;
  }
  
  .stats-icon {
    margin-right: 24px;
  }
  
  .stats-icon .icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--card-gradient);
    box-shadow: 
      0 15px 35px rgba(0, 0, 0, 0.1),
      0 5px 15px rgba(0, 0, 0, 0.07);
    position: relative;
    overflow: hidden;
  }
  
  .stats-icon .icon-wrapper::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%);
  }
  
  .stats-icon i {
    font-size: 32px;
    color: #fff;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
  }
  
  .stats-details {
    flex: 1;
  }
  
  .stats-number {
    font-size: 32px;
    font-weight: 800;
    color: #1e293b;
    line-height: 1.1;
    margin-bottom: 6px;
    background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .stats-label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .stats-trend {
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
  }
  
  .stats-trend i {
    font-size: 16px;
    margin-right: 6px;
    color: var(--card-color);
    opacity: 0.8;
  }

  /* Modern Modal Styles */
  .modern-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1055;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .modern-modal-dialog {
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
  }

  .modern-modal-content {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 24px;
    box-shadow: 
      0 25px 50px -12px rgba(0, 0, 0, 0.25),
      0 0 0 1px rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(20px);
    overflow: hidden;
  }

  .modern-modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 32px 32px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
  }

  .modern-modal-header::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  }

  .header-content {
    display: flex;
    align-items: center;
  }

  .header-icon {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
    backdrop-filter: blur(10px);
  }

  .header-icon i {
    font-size: 24px;
    color: white;
  }

  .modal-title {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 4px 0;
    color: white;
  }

  .modal-subtitle {
    font-size: 14px;
    margin: 0;
    opacity: 0.8;
    color: white;
  }

  .modern-close-btn {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .modern-close-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  .modern-modal-body {
    padding: 32px;
  }

  .info-card {
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
    height: 100%;
  }

  .info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  .info-card .card-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    font-size: 20px;
  }

  .amount-card .card-icon {
    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    color: white;
  }

  .plan-card .card-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  .status-card .card-icon {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: white;
  }

  .card-content {
    flex: 1;
  }

  .card-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    margin-bottom: 8px;
  }

  .card-value {
    font-size: 24px;
    font-weight: 800;
    color: #1e293b;
    line-height: 1;
    margin-bottom: 4px;
  }

  .card-currency {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 500;
  }

  .card-badge {
    margin-top: 4px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    margin: 32px 0;
  }

  .info-group {
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
  }

  .group-title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
  }

  .group-title i {
    color: #667eea;
  }

  .info-items {
    space-y: 16px;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .info-row:last-child {
    border-bottom: none;
  }

  .info-label {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
  }

  .info-value {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .description-section {
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin-top: 24px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
  }

  .section-title i {
    color: #667eea;
  }

  .description-content {
    font-size: 14px;
    line-height: 1.6;
    color: #475569;
    background: #f8fafc;
    padding: 16px;
    border-radius: 12px;
    border-left: 4px solid #667eea;
  }

  .modern-modal-footer {
    padding: 24px 32px;
    background: #f8fafc;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid #e2e8f0;
  }

  .btn-modern {
    padding: 12px 24px;
    border-radius: 12px;
    border: none;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
  }

  .btn-modern.btn-secondary {
    background: #e2e8f0;
    color: #475569;
  }

  .btn-modern.btn-secondary:hover {
    background: #cbd5e1;
    transform: translateY(-1px);
  }

  .btn-modern.btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }

  .btn-modern.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
  }
  
  @media (max-width: 768px) {
    .stats-card {
      height: 140px;
    }
    
    .stats-info {
      padding: 20px 16px;
    }
    
    .stats-icon {
      margin-right: 16px;
    }
    
    .stats-icon .icon-wrapper {
      width: 60px;
      height: 60px;
      border-radius: 16px;
    }
    
    .stats-icon i {
      font-size: 24px;
    }
    
    .stats-number {
      font-size: 24px;
    }
    
    .stats-label {
      font-size: 12px;
    }

    /* Modal Responsive */
    .modern-modal-dialog {
      margin: 1vh auto;
      max-width: 95%;
    }

    .modern-modal-header {
      padding: 24px 20px 20px;
    }

    .modern-modal-body {
      padding: 20px;
    }

    .modern-modal-footer {
      padding: 16px 20px;
      flex-direction: column;
    }

    .info-grid {
      grid-template-columns: 1fr;
      gap: 20px;
      margin: 20px 0;
    }

    .info-card {
      padding: 20px;
    }

    .card-value {
      font-size: 20px;
    }

    .btn-modern {
      width: 100%;
      justify-content: center;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('company-report-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'company-report-styles';
  styleElement.textContent = modalStyles;
  document.head.appendChild(styleElement);
}

const CompanyReport = () => {
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [filteredPaymentHistory, setFilteredPaymentHistory] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<PaymentRecord | null>(null);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    paymentStatus: '',
    planType: ''
  });

  // Filtreleri PaymentFilters component'i için uyumlu hale getir
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const applyFilters = useCallback(() => {
    let filtered = [...paymentHistory];

    // Date range filter - Tarihe göre filtreleme
    if (filters.startDate) {
      const startDateMoment = moment(filters.startDate);
      filtered = filtered.filter(payment => {
        const paymentDate = moment(payment.paidAt);
        return paymentDate.isSameOrAfter(startDateMoment, 'day');
      });
    }

    if (filters.endDate) {
      const endDateMoment = moment(filters.endDate);
      filtered = filtered.filter(payment => {
        const paymentDate = moment(payment.paidAt);
        return paymentDate.isSameOrBefore(endDateMoment, 'day');
      });
    }

    // Payment status filter - Status durumuna göre filtreleme
    if (filters.paymentStatus) {
      filtered = filtered.filter(payment => {
        return payment.paymentStatus === filters.paymentStatus;
      });
    }

    // Plan type filter
    if (filters.planType) {
      filtered = filtered.filter(payment => {
        return payment.type === filters.planType;
      });
    }

    setFilteredPaymentHistory(filtered);
  }, [paymentHistory, filters]);

  useEffect(() => {
    fetchPaymentData();
  }, []);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      paymentStatus: '',
      planType: ''
    });
    setDateRange(null);
  };



  // PaymentFilters component'i için handler'lar
  const handleDateRangeChange = (dates: [string, string] | null) => {
    setDateRange(dates);
    if (dates) {
      setFilters(prev => ({
        ...prev,
        startDate: dayjs(dates[0]).format('YYYY-MM-DD'),
        endDate: dayjs(dates[1]).format('YYYY-MM-DD')
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        startDate: '',
        endDate: ''
      }));
    }
  };

  const handlePaymentStatusChange = (status: string) => {
    setFilters(prev => ({
      ...prev,
      paymentStatus: status
    }));
  };

  const handlePlanTypeChange = (planType: string) => {
    setFilters(prev => ({
      ...prev,
      planType: planType
    }));
  };

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const historyData = await paymentService.getPaymentHistory();
      // Başlangıç tarihine göre sıralama (en geç başlangıç tarihi başta)
      const sortedData = historyData.sort((a, b) => {
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      });
      setPaymentHistory(sortedData);
      setFilteredPaymentHistory(sortedData);
    } catch (error) {
      // Handle error through error boundary or show user feedback
    } finally {
      setLoading(false);
    }
  };

  const calculateRemainingDays = (expiryDate: string) => {
    const today = moment();
    const expiry = moment(expiryDate);
    const diffDays = expiry.diff(today, 'days');
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      paid: {
        class: 'badge bg-success-light text-success border-success',
        icon: 'ti-check-circle',
        text: 'Ödendi'
      },
      pending: {
        class: 'badge bg-warning-light text-warning border-warning',
        icon: 'ti-clock',
        text: 'Beklemede'
      },
      failed: {
        class: 'badge bg-danger-light text-danger border-danger',
        icon: 'ti-x-circle',
        text: 'Başarısız'
      },
      refunded: {
        class: 'badge bg-info-light text-info border-info',
        icon: 'ti-refresh',
        text: 'İade Edildi'
      }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      class: 'badge bg-secondary-light text-secondary border-secondary',
      icon: 'ti-help-circle',
      text: status
    };

    return (
      <span className={`${config.class} d-inline-flex align-items-center px-3 py-2`}
        style={{ borderRadius: '8px', fontWeight: '500', fontSize: '0.9rem', minHeight: '28px' }}>
        <i className={`${config.icon} me-2`} style={{ fontSize: '1rem' }}></i>
        {config.text}
      </span>
    );
  };

  const getPlanTypeBadge = (type: string) => {
    const typeConfig = {
      premium: { color: '#0d6efd', text: 'Premium' },
      basic: { color: '#6c757d', text: 'Temel' },
      enterprise: { color: '#fd7e14', text: 'Kurumsal' },
      trial: { color: '#17a2b8', text: 'Deneme' }
    };

    const config = typeConfig[type as keyof typeof typeConfig] || {
      color: '#6c757d',
      text: type
    };

    return (
      <span
        className="badge px-3 py-1"
        style={{
          backgroundColor: config.color,
          color: '#fff',
          borderRadius: '6px',
          fontWeight: '500',
          fontSize: '0.875rem',
          textTransform: 'capitalize'
        }}>
        {config.text}
      </span>
    );
  };

  const columns = [
    // {
    //   title: 'Ödeme ID',
    //   dataIndex: 'id',
    //   key: 'id',
    //   render: (text: string) => (
    //     <span className="text-truncate" style={{ maxWidth: '100px' }}>
    //       {text}
    //     </span>
    //   )
    // },
    {
      title: 'Plan Türü',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => getPlanTypeBadge(type)
    },
    {
      title: 'Tutar',
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      sorter: (a: PaymentRecord, b: PaymentRecord) => b.amountPaid - a.amountPaid,
      render: (amount: number, record: PaymentRecord) => (
        <div className="d-flex align-items-center">
          <div className="payment-amount">
            <div className="d-flex align-items-center">
              <i className="ti ti-currency-lira me-2 text-success" style={{ fontSize: '1.2rem' }}></i>
              <div>
                <span className="amount-value text-primary fw-bold" style={{ fontSize: '1.1rem' }}>
                  {amount.toLocaleString('tr-TR')}
                </span>
                <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>{record.currency}</small>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Kullanıcı Limiti',
      dataIndex: 'purchasedLimit',
      key: 'purchasedLimit',
      render: (limit: number) => (
        <div className="user-limit-display">
          <div className="d-flex align-items-center">
            <div className="limit-icon me-2">
              <i className="ti ti-users text-info" style={{ fontSize: '1.4rem' }}></i>
            </div>
            <div>
              <span className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>{limit}</span>
              <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Kullanıcı</small>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Başlangıç Tarihi',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (a: PaymentRecord, b: PaymentRecord) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
      render: (date: string) => (
        <div className="d-flex align-items-center">
          <i className="ti ti-calendar-event me-2 text-success"></i>
          <span className="fw-semibold">{moment(date).format('DD.MM.YYYY')}</span>
        </div>
      )
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      sorter: (a: PaymentRecord, b: PaymentRecord) => new Date(b.expiryDate).getTime() - new Date(a.expiryDate).getTime(),
      render: (date: string) => {
        const remainingDays = calculateRemainingDays(date);
        const isExpiringSoon = remainingDays <= 30 && remainingDays > 0;
        const isExpired = remainingDays === 0;

        return (
          <div className="d-flex align-items-center">
            <i className={`ti ti-calendar-x me-2 ${isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : 'text-info'}`}></i>
            <div>
              <div className="fw-semibold">{moment(date).format('DD.MM.YYYY')}</div>
              <small className={`${isExpired ? 'text-danger' : isExpiringSoon ? 'text-warning' : 'text-muted'}`}>
                {remainingDays === 0 ? 'Süresi doldu' : `${remainingDays} gün kaldı`}
              </small>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Durum',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: string) => getStatusBadge(status)
    },
    {
      title: 'Ödeme Tarihi',
      dataIndex: 'paidAt',
      key: 'paidAt',
      sorter: (a: PaymentRecord, b: PaymentRecord) => {
        if (!a.paidAt) return 1;
        if (!b.paidAt) return -1;
        return new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime();
      },
      render: (date: string) => date ? (
        <div className="d-flex align-items-center">
          <i className="ti ti-calendar-check me-2 text-primary"></i>
          <div>
            <div className="fw-semibold">{moment(date).format('DD.MM.YYYY')}</div>
            <small className="text-muted">{moment(date).format('HH:mm')}</small>
          </div>
        </div>
      ) : (
        <span className="text-muted">-</span>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: PaymentRecord) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <button
              className="btn btn-sm btn-info me-2 rounded-pill"
              onClick={() => setSelectedInvoice(record)}
              title="Fatura Görüntüle"
              style={{ padding: '0.25rem 0.75rem' }}
            >
              <i className="ti ti-eye me-1" />
              Görüntüle
            </button>
            <button
              className="btn btn-sm btn-success rounded-pill"
              onClick={() => handleDownloadInvoice(record)}
              title="Fatura İndir"
              style={{ padding: '0.25rem 0.75rem' }}
            >
              <i className="ti ti-download me-1" />
              İndir
            </button>
          </div>
        </div>
      )
    }
  ];

  const handleDownloadInvoice = (record: PaymentRecord) => {
    // PDF fatura oluştur ve indir
    const generatePDFContent = () => {
      const planTypeText = {
        premium: 'Premium',
        basic: 'Temel',
        enterprise: 'Kurumsal',
        trial: 'Deneme'
      }[record.type] || record.type;

      const statusText = {
        paid: 'Ödendi',
        pending: 'Beklemede',
        failed: 'Başarısız',
        refunded: 'İade Edildi'
      }[record.paymentStatus] || record.paymentStatus;

      return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Fatura - ${record.id}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
        .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #007bff; padding-bottom: 20px; }
        .company-name { font-size: 24px; font-weight: bold; color: #007bff; }
        .invoice-title { font-size: 18px; margin-top: 10px; }
        .invoice-info { display: flex; justify-content: space-between; margin: 30px 0; }
        .info-section { flex: 1; margin-right: 20px; }
        .info-title { font-weight: bold; color: #333; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .info-row { margin: 8px 0; }
        .label { font-weight: bold; display: inline-block; width: 140px; }
        .value { color: #666; }
        .amount { font-size: 20px; font-weight: bold; color: #28a745; }
        .status-paid { color: #28a745; font-weight: bold; }
        .status-pending { color: #ffc107; font-weight: bold; }
        .status-failed { color: #dc3545; font-weight: bold; }
        .description { margin-top: 30px; padding: 20px; background-color: #f8f9fa; border-radius: 5px; }
        .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 20px; }
    </style>
</head>
<body>
    <div class="header">
        <div class="company-name">DinoxWeb</div>
        <div class="invoice-title">FATURA</div>
    </div>
    
    <div class="invoice-info">
        <div class="info-section">
            <div class="info-title">Ödeme Bilgileri</div>
            <div class="info-row">
                <span class="label">Fatura ID:</span>
                <span class="value">${record.id}</span>
            </div>
            <div class="info-row">
                <span class="label">Tutar:</span>
                <span class="value amount">${record.amountPaid.toLocaleString('tr-TR')} ${record.currency}</span>
            </div>
            <div class="info-row">
                <span class="label">Plan Türü:</span>
                <span class="value">${planTypeText}</span>
            </div>
            <div class="info-row">
                <span class="label">Kullanıcı Limiti:</span>
                <span class="value">${record.purchasedLimit} Kullanıcı</span>
            </div>
            <div class="info-row">
                <span class="label">Durum:</span>
                <span class="value status-${record.paymentStatus}">${statusText}</span>
            </div>
        </div>
        
        <div class="info-section">
            <div class="info-title">Süre Bilgileri</div>
            <div class="info-row">
                <span class="label">Başlangıç:</span>
                <span class="value">${moment(record.startDate).format('DD.MM.YYYY')}</span>
            </div>
            <div class="info-row">
                <span class="label">Bitiş:</span>
                <span class="value">${moment(record.expiryDate).format('DD.MM.YYYY')}</span>
            </div>
            <div class="info-row">
                <span class="label">Süre:</span>
                <span class="value">${record.durationDays} gün</span>
            </div>
            <div class="info-row">
                <span class="label">Ödeme Tarihi:</span>
                <span class="value">${record.paidAt ? moment(record.paidAt).format('DD.MM.YYYY HH:mm') : '-'}</span>
            </div>
        </div>
    </div>
    
    ${record.description ? `
    <div class="description">
        <div class="info-title">Açıklama</div>
        <p>${record.description}</p>
    </div>
    ` : ''}
    
    <div class="footer">
        <p>Bu fatura ${moment().format('DD.MM.YYYY HH:mm')} tarihinde otomatik olarak oluşturulmuştur.</p>
        <p>DinoxWeb - Şirket Yönetim Sistemi</p>
    </div>
</body>
</html>`;
    };

    // HTML'i blob olarak oluştur ve direkt indir
    const htmlContent = generatePDFContent();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    // Direkt HTML dosyası olarak indir (preview açılmaz)
    const element = document.createElement('a');
    element.href = url;
    element.download = `fatura-${record.id}-${moment(record.paidAt || new Date()).format('YYYY-MM-DD')}.html`;
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    // URL'i temizle
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Yükleniyor...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-1">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Şirket Ödeme Raporu</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Raporlar</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Şirket Ödeme Raporu
                </li>
              </ol>
            </nav>
          </div>

        </div>

        {/* Modern İstatistik Kartları */}
        <div className="row g-4 mb-4">
          {/* Toplam Ödenen */}
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="stats-card stats-card-primary">
              <div className="card-body p-0">
                <div className="stats-info">
                  <div className="stats-icon">
                    <div className="icon-wrapper">
                      <i className="ti ti-currency-dollar"></i>
                    </div>
                  </div>
                  <div className="stats-details">
                    <div className="stats-number">
                      ₺{filteredPaymentHistory
                        .filter(payment => payment.paymentStatus === 'paid')
                        .reduce((sum, payment) => sum + payment.amountPaid, 0)
                        .toLocaleString('tr-TR')}
                    </div>
                    <div className="stats-label">Toplam Ödenen Tutar</div>
                    <div className="stats-trend">

                      <i className="ti ti-check"></i>

                      <span>Sadece Ödenmiş</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Toplam Ödeme Sayısı */}
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="stats-card stats-card-success">
              <div className="card-body p-0">
                <div className="stats-info">
                  <div className="stats-icon">
                    <div className="icon-wrapper">
                      <i className="ti ti-list-numbers"></i>
                    </div>
                  </div>
                  <div className="stats-details">
                    <div className="stats-number">
                      {filteredPaymentHistory.length}
                    </div>
                    <div className="stats-label">Toplam Ödeme Sayısı</div>
                    <div className="stats-trend">
                      <i className="ti ti-check"></i>
                      <span>Tüm Kayıtlar</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kalan Günler */}
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="stats-card stats-card-warning">
              <div className="card-body p-0">
                <div className="stats-info">
                  <div className="stats-icon">
                    <div className="icon-wrapper">
                      <i className="ti ti-calendar-time"></i>
                    </div>
                  </div>
                  <div className="stats-details">
                    <div className="stats-number">
                      {filteredPaymentHistory.filter(p => p.paymentStatus === 'paid').length > 0
                        ? Math.max(...filteredPaymentHistory
                          .filter(p => p.paymentStatus === 'paid')
                          .map(p => calculateRemainingDays(p.expiryDate)))
                        : 0}
                    </div>
                    <div className="stats-label">Kalan Günler</div>
                    <div className="stats-trend">
                      <i className="ti ti-check"></i>
                      <span>Ödenmiş Paketler</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Kullanıcı Limiti */}
          <div className="col-xl-3 col-sm-6 col-12">
            <div className="stats-card stats-card-info">
              <div className="card-body p-0">
                <div className="stats-info">
                  <div className="stats-icon">
                    <div className="icon-wrapper">
                      <i className="ti ti-users-group"></i>
                    </div>
                  </div>
                  <div className="stats-details">
                    <div className="stats-number">
                      {filteredPaymentHistory
                        .filter(payment => payment.paymentStatus === 'paid')
                        .filter(payment => calculateRemainingDays(payment.expiryDate) > 0)
                        .reduce((sum, payment) => sum + payment.purchasedLimit, 0)}
                    </div>
                    <div className="stats-label">Toplam Kullanıcı Limiti</div>
                    <div className="stats-trend">
                      <i className="ti ti-check"></i>
                      <span>Aktif Kapasite</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ödeme Geçmişi Tablosu */}
        <div className="row">
          <div className="col-sm-12">
            <div className="card card-table comman-shadow">
              {/* Tarih Aralığı Göstergesi */}
              {dateRange && (
                <div className="card-body border-bottom pt-2 pb-2 bg-light">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-calendar-event fs-16 me-2 text-primary"></i>
                    <span className="fw-semibold text-muted me-1">Tarih Aralığı:</span>
                    <span className="text-dark fw-bold">
                      {dayjs(dateRange[0]).format('DD MMMM YYYY')} - {dayjs(dateRange[1]).format('DD MMMM YYYY')}
                    </span>
                  </div>

                </div>

              )}

              {/* Tablo Header */}
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div>
                  <h3 className="page-title mb-1">Ödeme Geçmişi</h3>
                  <p className="text-muted mb-0">
                    <i className="ti ti-info-circle me-1"></i>
                    En geç başlangıç tarihi olan kayıtlar başta gösterilmektedir. Sütun başlıklarına tıklayarak sıralama yapabilirsiniz.
                  </p>

                </div>

                <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                  <div className="d-flex align-items-end flex-wrap gap-2">
                    {/* Kayıt Sayısı */}
                    <span className="badge bg-primary-light text-primary px-2 py-1">
                      {filteredPaymentHistory.length} / {paymentHistory.length}
                    </span>
                  </div>
                  <PaymentFilters
                    selectedDateRange={dateRange}
                    selectedPaymentStatus={filters.paymentStatus}
                    selectedPlanType={filters.planType}
                    onDateRangeChange={handleDateRangeChange}
                    onPaymentStatusChange={handlePaymentStatusChange}
                    onPlanTypeChange={handlePlanTypeChange}
                    onReset={resetFilters}
                  />

                </div>


              </div>

              <div className="card-body">
                <Datatable
                  columns={columns}
                  dataSource={filteredPaymentHistory}
                  Selection={false}
                  showPageSize={false}
                  showSearch={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modern Fatura Modal */}
        {selectedInvoice && (
          <div
            className="modern-modal-backdrop"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.7) 100%)',
              backdropFilter: 'blur(8px)',
              animation: 'fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            onClick={() => setSelectedInvoice(null)}
          >
            <div
              className="modern-modal-dialog"
              onClick={(e) => e.stopPropagation()}
              style={{
                animation: 'modalSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                maxWidth: '900px',
                margin: '2vh auto'
              }}
            >
              <div className="modern-modal-content">
                {/* Modern Header */}
                <div className="modern-modal-header">
                  <div className="header-content">
                    <div className="header-icon">
                      <i className="ti ti-receipt"></i>
                    </div>
                    <div className="header-info">
                      <h4 className="modal-title">Fatura Detayları</h4>
                      <p className="modal-subtitle">#{selectedInvoice.id.slice(-8)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="modern-close-btn"
                    onClick={() => setSelectedInvoice(null)}
                  >
                    <i className="ti ti-x"></i>
                  </button>
                </div>

                {/* Modern Body */}
                <div className="modern-modal-body">
                  {/* Primary Info Cards */}
                  <div className="row g-4 mb-4">
                    <div className="col-md-4">
                      <div className="info-card amount-card">
                        <div className="card-icon">
                          <i className="ti ti-currency-dollar"></i>
                        </div>
                        <div className="card-content">
                          <div className="card-label">Ödenen Tutar</div>
                          <div className="card-value">
                            ₺{selectedInvoice.amountPaid.toLocaleString('tr-TR')}
                          </div>
                          <div className="card-currency">{selectedInvoice.currency}</div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="info-card plan-card">
                        <div className="card-icon">
                          <i className="ti ti-package"></i>
                        </div>
                        <div className="card-content">
                          <div className="card-label">Plan Türü</div>
                          <div className="card-badge">
                            {getPlanTypeBadge(selectedInvoice.type)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="info-card status-card">
                        <div className="card-icon">
                          <i className="ti ti-shield-check"></i>
                        </div>
                        <div className="card-content">
                          <div className="card-label">Durum</div>
                          <div className="card-badge">
                            {getStatusBadge(selectedInvoice.paymentStatus)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Secondary Info Grid */}
                  <div className="info-grid">
                    <div className="info-group">
                      <h6 className="group-title">
                        <i className="ti ti-calendar me-2"></i>
                        Süre Bilgileri
                      </h6>
                      <div className="info-items">
                        <div className="info-row">
                          <span className="info-label">Başlangıç</span>
                          <span className="info-value">{moment(selectedInvoice.startDate).format('DD.MM.YYYY')}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Bitiş</span>
                          <span className="info-value">{moment(selectedInvoice.expiryDate).format('DD.MM.YYYY')}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Toplam Süre</span>
                          <span className="info-value text-info">{selectedInvoice.durationDays} gün</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Kalan Süre</span>
                          <span className="info-value text-warning">{calculateRemainingDays(selectedInvoice.expiryDate)} gün</span>
                        </div>
                      </div>
                    </div>

                    <div className="info-group">
                      <h6 className="group-title">
                        <i className="ti ti-info-circle me-2"></i>
                        Ek Bilgiler
                      </h6>
                      <div className="info-items">
                        <div className="info-row">
                          <span className="info-label">Kullanıcı Limiti</span>
                          <span className="info-value">{selectedInvoice.purchasedLimit} kişi</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Ödeme Tarihi</span>
                          <span className="info-value">
                            {selectedInvoice.paidAt ? moment(selectedInvoice.paidAt).format('DD.MM.YYYY HH:mm') : 'Belirtilmemiş'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {selectedInvoice.description && (
                    <div className="description-section">
                      <h6 className="section-title">
                        <i className="ti ti-file-text me-2"></i>
                        Açıklama
                      </h6>
                      <div className="description-content">
                        {selectedInvoice.description}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modern Footer */}
                <div className="modern-modal-footer">
                  <button
                    type="button"
                    className="btn-modern btn-secondary"
                    onClick={() => setSelectedInvoice(null)}
                  >
                    <i className="ti ti-x me-2"></i>
                    Kapat
                  </button>
                  <button
                    type="button"
                    className="btn-modern btn-primary"
                    onClick={() => handleDownloadInvoice(selectedInvoice)}
                  >
                    <i className="ti ti-download me-2"></i>
                    Fatura İndir
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyReport;
